import { Component, computed, effect, inject, signal, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { NbDateService, NbDialogService, NbToastrService } from '@nebular/theme';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DeleteDialogComponent } from '@components/dialog/delete-dialog/delete-dialog.component';
import { GeneratedPasswordDialogComponent } from '@components/dialog/generated-password-dialog/generated-password-dialog.component';
import {
  AddressMergeDto,
  AddressService,
  BuildingFilterDto,
  ContractorExternalConfigMergeDto,
  TenantDetailDto,
  TenantMergeDto,
  TenantService,
} from '@artemis-software/wr-api';
import { firstValueFrom } from 'rxjs';
import { PendingChanges } from '@guards/pending-changes.guard';
import { CurrentUserService } from '@services/current-user.service';
import { currentTenant } from '@/utils/admin-utils';
import { loader } from '@/utils/signals/loader';
import { tryWithLoadingSignal } from '@/utils/async-utils';
import { queryParam, routeParam } from '@/utils/signals/router';
import { asyncComputed } from '@/utils/signals/asyncComputed';
import {
  attachmentDetailToAttachmentReferenceDto,
  attachmentFromDetail,
} from '@shared/form-controls/attachment-upload/attachment-utils';
import { ContactPersonDialogComponent } from '@components/dialog/contact-person-dialog/contact-person-dialog.component';
import TenantTypeEnum = BuildingFilterDto.TenantTypeEnum;
import {
  TenantGuidelinesConfigComponent
} from '@pages/tenant-detail-page/tenant-guidelines-config/tenant-guidelines-config.component';
import { defaultPdfOptions } from '@/models/inspection-pdf-options';

@Component({
  standalone: false,
  selector: 'wr-tenant-detail-page',
  templateUrl: './tenant-detail-page.component.html',
  styleUrls: ['./tenant-detail-page.component.scss'],
})
export class TenantDetailPageComponent implements PendingChanges {

  private readonly router = inject(Router);
  private readonly dialogService = inject(NbDialogService);
  private readonly tenantService = inject(TenantService);
  private readonly addressService = inject(AddressService);
  private readonly dateService = inject(NbDateService);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly toastrService = inject(NbToastrService);

  readonly tenantId = routeParam('id', true);
  readonly tenantTypeParam = queryParam('tenantType');
  readonly tabParam = queryParam('tab');

  readonly saving = loader();
  readonly loading = loader();
  readonly canEdit = signal(false);
  readonly isEditing = signal(false);
  readonly firstSubmit = signal(false);
  readonly currentTab = signal('Grunddaten');
  readonly form = signal<FormGroup | undefined>(undefined);

  readonly isNew = computed(() => this.tenantId() === 'new');
  readonly canSeeFolders = computed(() => this.currentUserService.hasPrivilege('viewAttachmentFolders'));
  readonly canManageCorporateGroup = computed(() => this.currentUserService.hasPrivilege('viewCorporateGroup'));
  readonly canViewGuidelines = computed(() => this.currentUserService.hasPrivilege('viewExternalTenantGuidelines'));
  readonly canEditGuidelines = computed(() => this.currentUserService.hasPrivilege('editExternalTenantGuidelines'));
  readonly canEditHiddenPdfTypes = computed(() => this.tenant()?.permissions.editHiddenInspectionPdfTypes ?? false);
  readonly canEditCatalogSource = computed(() => this.tenant()?.permissions.editCatalogSourceTenant ?? false);
  readonly canEditIncompleteUpload = computed(() => this.tenant()?.permissions.editIncompleteInspectionUpload ?? false);
  readonly onFolderTab = computed(() => this.currentTab() === 'Gebäude Ordner' || this.currentTab() === 'TGA Ordner');
  readonly currentTenant = currentTenant();

  @ViewChild(TenantGuidelinesConfigComponent) private readonly guidelinesConfig?: TenantGuidelinesConfigComponent;

  readonly tenantType = computed(() => {
    const mode = this.tenantTypeParam();
    if (mode === 'external') return TenantTypeEnum.External;
    if (mode === 'internal') return TenantTypeEnum.Internal;
    return TenantTypeEnum.Contractor;
  });

  readonly tenant = asyncComputed(() => {
    const id = this.tenantId();
    if (id === 'new') return undefined;
    return this.tenantService.findById(id);
  }, undefined, this.loading);

  readonly dateTimeFormat = 'yyyy-MM-ddTHH:mm:ss.SSSZ';
  readonly dateFormat = 'yyyy-MM-dd';

  constructor() {
    effect(() => {
      this.currentTab.set(this.tabParam() ?? 'Grunddaten');
    });

    effect(() => {
      const tenant = this.tenant();
      if (this.isNew()) {
        this.isEditing.set(true);
        this.canEdit.set(true);
        this.initForm();
        return;
      }
      if (!tenant) return;
      this.canEdit.set(tenant.permissions.editTenant);
      this.initForm(tenant);
      this.form()?.disable();
    });
  }

  initForm(dto?: TenantDetailDto): void {
    let contractDate = undefined;
    if (dto?.contractDate) {
      contractDate = this.dateService.parse(dto.contractDate, this.dateTimeFormat);
    }
    this.form.set(new FormGroup({
      id: new FormControl(dto?.id),
      name: new FormControl(dto?.name, [Validators.required]),
      contractDateText: new FormControl(contractDate),
      contractDate: new FormControl(undefined),

      paymentAddressId: new FormControl(dto?.paymentAddress?.id),
      street: new FormControl(dto?.paymentAddress?.street, [Validators.required]),
      number: new FormControl(dto?.paymentAddress?.number, [Validators.required]),
      zipCode: new FormControl(dto?.paymentAddress?.zipCode, [Validators.required]),
      city: new FormControl(dto?.paymentAddress?.city, [Validators.required]),
      state: new FormControl(dto?.paymentAddress?.state, [Validators.required]),
      country: new FormControl({ value: 'AT', disabled: true }),
      color: new FormControl(dto?.color, [Validators.required]),

      email: new FormControl(dto?.email, [Validators.email, Validators.required]),
      phone: new FormControl(dto?.phone, [Validators.required]),
      description: new FormControl(dto?.description),
      pin: new FormControl(dto?.pin),
      contractorExternalConfigId: new FormControl(null),
      contractorNumber: new FormControl(''),
      facilityElements: new FormControl(null),
      smallLogoReference: new FormControl(attachmentFromDetail(dto?.smallLogo)),
      largeLogoReference: new FormControl(attachmentFromDetail(dto?.largeLogo)),
      homePage: new FormControl(dto?.homePage),
      companyRegisterNumber: new FormControl(dto?.companyRegisterNumber),
      iban: new FormControl(dto?.iban),
      bic: new FormControl(dto?.bic),
      court: new FormControl(dto?.court),
      worksWithOenorm: new FormControl(dto?.worksWithOenorm),
      usesRiskAssessmentByDefault: new FormControl(dto?.usesRiskAssessmentByDefault ?? true),
      allowIncompleteInspectionUpload: new FormControl(dto?.allowIncompleteInspectionUpload),
      corporateGroupId: new FormControl(dto?.corporateGroupId),
      catalogSourceTenantId: new FormControl(dto?.catalogSourceTenantId),
      signatureReference: new FormControl(attachmentFromDetail(dto?.signature)),
      certificateTitle: new FormControl(dto?.certificateTitle),
      signatureName: new FormControl(dto?.signatureName),
      longListTitle: new FormControl(dto?.longListTitle),
      nameAddition: new FormControl(dto?.nameAddition),
      contactPerson: new FormControl(dto?.contactPerson ?? null),
      embeddedContactPerson: new FormControl(dto?.embeddedContactPerson ?? null),
      hiddenInspectionPdfTypes: new FormControl(dto?.hiddenInspectionPdfTypes ? [...dto.hiddenInspectionPdfTypes] : []),
      emailRequiredInspectionPdfTypes: new FormControl(dto?.emailRequiredInspectionPdfTypes ? [...dto.emailRequiredInspectionPdfTypes] : []),
      horaPassInspectionPdfTypes: new FormControl(dto?.horaPassInspectionPdfTypes ? [...dto.horaPassInspectionPdfTypes] : []),
    }));
  }

  hasError(controlName: string, errorName = 'required'): boolean {
    const control = this.form()?.get(controlName);
    return this.isEditing() && this.firstSubmit() && (control?.hasError(errorName) ?? false) && ((control?.touched ?? false) || (control?.dirty ?? false));
  }

  private readonly fieldTabLabels: Record<string, { tab: string; label: string }> = {
    name: { tab: 'Grunddaten', label: 'Name' },
    color: { tab: 'Grunddaten', label: 'Farbe' },
    email: { tab: 'Grunddaten', label: 'Email' },
    phone: { tab: 'Grunddaten', label: 'Telefonnummer' },
    street: { tab: 'Grunddaten', label: 'Straße' },
    number: { tab: 'Grunddaten', label: 'Nummer' },
    zipCode: { tab: 'Grunddaten', label: 'Postleitzahl' },
    city: { tab: 'Grunddaten', label: 'Stadt' },
    state: { tab: 'Grunddaten', label: 'Bundesland' },
  };

  private invalidFieldsMessage(form: FormGroup): string {
    const byTab = new Map<string, string[]>();
    for (const [controlName, control] of Object.entries(form.controls)) {
      if (!control.invalid) continue;
      const field = this.fieldTabLabels[controlName];
      if (!field) continue;
      byTab.set(field.tab, [...(byTab.get(field.tab) ?? []), field.label]);
    }
    if (byTab.size === 0) {
      return 'Kann nicht gespeichert werden, es gibt noch Fehler.';
    }
    const parts = [...byTab.entries()].map(([tab, labels]) => `Tab "${tab}": ${labels.join(', ')}`);
    return `Kann nicht gespeichert werden. Fehlende oder fehlerhafte Felder — ${parts.join('; ')}.`;
  }

  onEdit(): void {
    this.isEditing.set(true);
    this.form()?.enable();
    this.form()?.get('country')?.disable();
  }

  tabChanged(event: any): void {
    this.currentTab.set(event.tabTitle);
    this.router.navigate([], {
      queryParams: { tab: event.tabTitle },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  async submit(): Promise<void> {
    this.firstSubmit.set(true);
    const form = this.form();
    form?.markAllAsTouched();
    form?.updateValueAndValidity({ onlySelf: false, emitEvent: false });

    if (!form?.valid) {
      if (form) {
        this.toastrService.danger(this.invalidFieldsMessage(form), 'Fehler', { duration: 8000 });
      }
      return;
    }

    await tryWithLoadingSignal(this.saving, async () => {
      try {
        const addressDto: AddressMergeDto = {
          id: form.get('paymentAddressId')?.value,
          street: form.get('street')?.value,
          number: form.get('number')?.value,
          zipCode: form.get('zipCode')?.value,
          city: form.get('city')?.value,
          state: form.get('state')?.value,
          country: form.get('country')?.value,
        };
        const address = await firstValueFrom(this.addressService.merge(addressDto));

        form.patchValue({ paymentAddressId: address.id });
        const contractDate = form.get('contractDateText')?.value;
        if (contractDate) {
          form.controls['contractDate'].setValue(this.dateService.format(contractDate, this.dateFormat));
        }

        const { contactPerson, ...formValues } = form.value;
        const tenantDto = {
          ...formValues,
          smallLogoReference: attachmentDetailToAttachmentReferenceDto(form.get('smallLogoReference')?.value),
          largeLogoReference: attachmentDetailToAttachmentReferenceDto(form.get('largeLogoReference')?.value),
          signatureReference: attachmentDetailToAttachmentReferenceDto(form.get('signatureReference')?.value),
          contactPersonId: contactPerson?.id ?? null,
        } as TenantMergeDto;
        tenantDto.tenantType = this.tenantType();
        // clearing the catalog source needs an explicit flag — a null merge field is ignored
        if (this.tenant()?.catalogSourceTenantId && !form.get('catalogSourceTenantId')?.value) {
          tenantDto.clearCatalogSourceTenant = true;
        }

        let tenant: TenantDetailDto;
        if (this.isNew()) {
          const result = await firstValueFrom(this.tenantService.create(tenantDto));
          tenant = result.tenant;
          this.showGeneratedPassword(result.adminUserName, result.generatedPassword);
        } else {
          tenant = await firstValueFrom(this.tenantService.merge(tenantDto));
        }

        const currentTenantId = this.currentTenant()?.id;
        const currentTenantType = this.currentTenant()?.type;
        if (currentTenantId && currentTenantType === TenantTypeEnum.External) {
          const contractorExternalConfigMergeDto: ContractorExternalConfigMergeDto = {
            id: form.get('contractorExternalConfigId')?.value,
            contractorId: tenant.id,
            externalTenantId: currentTenantId,
            contractorNumber: form.get('contractorNumber')?.value,
            facilityElements: form.get('facilityElements')?.value,
          };
          await firstValueFrom(this.tenantService.mergeContractorExternalConfig(contractorExternalConfigMergeDto));
        }

        await this.guidelinesConfig?.save();
        this.isEditing.set(false);
        await this.router.navigate(
          ['tenant', tenant.id],
          {
            queryParams: {
              tenantType: this.tenantTypeParam(),
              tab: this.currentTab(),
            },
            replaceUrl: true,
          },
        );
        this.toastrService.success('Kunde wurde erfolgreich gespeichert', 'Kunde');
        this.form()?.disable();
      } catch (e) {
        console.error(e);
        this.toastrService.danger('Kunde konnte nicht gespeichert werden.', 'Fehler');
      }
    });
  }

  private showGeneratedPassword(adminUserName: string, password: string): void {
    this.dialogService.open(GeneratedPasswordDialogComponent, {
      context: { password, userName: adminUserName },
      closeOnBackdropClick: false,
      closeOnEsc: false,
    });
  }

  async onDelete(): Promise<void> {
    const form = this.form();
    const ref = this.dialogService.open(DeleteDialogComponent, {
      context: {
        title: 'Kunde löschen',
        message: 'Bist du sicher, dass du die Kunde löschen willst?',
      },
    });

    const result = await firstValueFrom(ref.onClose);
    if (!result) return;

    try {
      await firstValueFrom(this.addressService.deleteById(form?.value.paymentAddressId));
      await firstValueFrom(this.tenantService.deleteById(form?.value.id));
      await this.router.navigate(['tenant']);
      this.toastrService.success('Kunde wurde erfolgreich gelöscht', 'Kunde');
    } catch {
      this.toastrService.danger('Beim Löschen der Kunde ist ein Fehler aufgetreten', 'Kunde');
    }
  }

  async cancel(): Promise<void> {
    await this.router.navigateByUrl('/', { skipLocationChange: true });
    await this.router.navigate(['tenant', this.form()?.value.id]);
  }

  async navigateBack(): Promise<void> {
    if (this.tenantType() === TenantTypeEnum.External) {
      await this.router.navigate(['externalTenants']);
    } else if (this.tenantType() === TenantTypeEnum.Internal) {
      await this.router.navigate(['internalTenants']);
    } else {
      await this.router.navigate(['contractorTenants']);
    }
  }

  hasPendingChanges(): boolean | Promise<boolean> {
    return this.isEditing();
  }

  getContactPersonName(): string {
    const contactPerson = this.form()?.get('contactPerson')?.value;
    if (contactPerson && (contactPerson.firstName || contactPerson.lastName)) {
      return `${contactPerson.firstName ?? ''} ${contactPerson.lastName ?? ''}`.trim();
    }
    const embedded = this.form()?.get('embeddedContactPerson')?.value;
    if (embedded && (embedded.firstName || embedded.lastName)) {
      return `${embedded.firstName ?? ''} ${embedded.lastName ?? ''}`.trim();
    }
    return '';
  }

  protected readonly TenantTypeEnum = TenantTypeEnum;
  protected readonly pdfOptions = defaultPdfOptions;

  isPdfTypeVisible(type: string): boolean {
    const hidden: string[] = this.form()?.get('hiddenInspectionPdfTypes')?.value ?? [];
    return !hidden.includes(type);
  }

  togglePdfType(type: string, visible: boolean): void {
    const control = this.form()?.get('hiddenInspectionPdfTypes');
    if (!control) return;
    const hidden = new Set<string>(control.value ?? []);
    if (visible) {
      hidden.delete(type);
    } else {
      hidden.add(type);
      this.toggleEmailRequired(type, false);
      this.toggleHoraPass(type, false);
    }
    control.setValue([...hidden]);
    control.markAsDirty();
  }

  isPdfTypeEmailRequired(type: string): boolean {
    return this.isPdfTypeSelected('emailRequiredInspectionPdfTypes', type);
  }

  toggleEmailRequired(type: string, required: boolean): void {
    this.setPdfTypeSelected('emailRequiredInspectionPdfTypes', type, required);
  }

  isPdfTypeHoraPass(type: string): boolean {
    return this.isPdfTypeSelected('horaPassInspectionPdfTypes', type);
  }

  toggleHoraPass(type: string, appended: boolean): void {
    this.setPdfTypeSelected('horaPassInspectionPdfTypes', type, appended);
  }

  private isPdfTypeSelected(controlName: string, type: string): boolean {
    const selected: string[] = this.form()?.get(controlName)?.value ?? [];
    return selected.includes(type);
  }

  private setPdfTypeSelected(controlName: string, type: string, selected: boolean): void {
    const control = this.form()?.get(controlName);
    if (!control) return;
    const types = new Set<string>(control.value ?? []);
    if (selected) {
      types.add(type);
    } else {
      types.delete(type);
    }
    control.setValue([...types]);
    control.markAsDirty();
  }

  openContactPersonDialog(): void {
    const contactPerson = this.form()?.get('contactPerson')?.value || this.form()?.get('embeddedContactPerson')?.value;
    this.dialogService.open(ContactPersonDialogComponent, {
      context: {
        contactPerson: contactPerson,
        tenantId: this.form()?.get('id')?.value,
      },
    }).onClose.subscribe((result) => {
      if (result?.contactPerson) {
        this.form()?.get('contactPerson')?.setValue(result.contactPerson);
        this.form()?.get('embeddedContactPerson')?.setValue(null);
        this.form()?.markAsDirty();
      } else if (result?.embeddedContactPerson) {
        this.form()?.get('embeddedContactPerson')?.setValue(result.embeddedContactPerson);
        this.form()?.get('contactPerson')?.setValue(null);
        this.form()?.markAsDirty();
      }
    });
  }

}
